// shared-lib/millwork/crown.js
/**
 * Crown along one wall, the way the 3D view draws it (spec §2): only where
 * crown is switched on and the wall has a flat top; an opening reaching the
 * ceiling, or a shower region reaching the top, stops it; so does a cabinet
 * (or anything else standing against the wall) whose top rises into the
 * crown — the crown stays on the wall and stops square at the cabinet's side,
 * as wall crown conventionally does. A cabinet's own crown is a separate
 * thing (the user, 2026-10-30). A cabinet under the crown's bottom edge
 * leaves the crown running along the wall above it.
 *
 * Pure.
 */
const { wallHasFlatTop } = require('../geometry/wallHeightStats');
const { wallLengthM, wallStartAxis, objAlongWall } = require('./wallFrame');
const { unionIntervals, subtractIntervals } = require('./intervals');
const { TRIM_STYLE_PRESETS, trimStyleOf } = require('./defaults');
const { MIN_SPAN_M } = require('./baseboard');
const { regionRectOnWall } = require('./regions');
const { crownTopsById } = require('../cabinet-trim/pieces');

/** An opening or shower region within this of the ceiling stops it (3D's 0.15). */
const CROWN_CLEARANCE_M = 0.15;
const END_EPS_M = 0.005;

/**
 * Where along the wall (metres from `start`) cabinets stop a crown of this
 * style: each object on the wall whose top rises above the crown's bottom
 * edge, touching ones merged into one stretch. Reads only `.x` / `.z` of
 * `start` / `end`.
 */
const cabinetRegionsOnWall = (wallId, start, end, ceilingY, style, room) => {
  if (!room?.objects || !start || !end) return [];
  const vx = end.x - start.x;
  const vz = end.z - start.z;
  const wallLength = Math.hypot(vx, vz);
  if (wallLength < 0.01) return [];
  const dx = vx / wallLength;
  const dz = vz / wallLength;
  const crownBottomY =
    ceilingY - TRIM_STYLE_PRESETS[trimStyleOf({ style })].crownHeight;
  // a cabinet carrying its own crown reaches as high as that crown does
  // (`shared-lib/cabinet-trim`), so the two never run into each other
  const cabinetCrownTops = crownTopsById(room, { ceilingY });
  const regions = [];
  for (const obj of room.objects) {
    if (!obj?.center || !obj?.dimensions) continue;
    if (
      !Array.isArray(obj.parentWallIds) ||
      !obj.parentWallIds.includes(wallId)
    )
      continue;
    if (obj.type === 'crown-molding' || obj.type === 'blank-panel') continue;
    const top =
      cabinetCrownTops.get(obj.id) ??
      (obj.center.y || 0) + (obj.dimensions.y || 0) / 2;
    if (top <= crownBottomY + END_EPS_M) continue;
    const param =
      ((obj.center.x || 0) - start.x) * dx +
      ((obj.center.z || 0) - start.z) * dz;
    let extent = obj.dimensions.x || 0;
    if (obj.type === 'corner-cabinet' && obj.parentWallIds.length === 2) {
      const idx = obj.parentWallIds.indexOf(wallId);
      if (idx === 0) extent = obj.dimensions.alongA || obj.dimensions.x || 0;
      else if (idx === 1)
        extent = obj.dimensions.alongB || obj.dimensions.z || 0;
    }
    if (extent <= 0) continue;
    const s = Math.max(0, param - extent / 2);
    const e = Math.min(wallLength, param + extent / 2);
    if (e <= s + END_EPS_M) continue;
    regions.push({ start: s, end: e });
  }
  regions.sort((a, b) => a.start - b.start);
  const merged = [];
  for (const r of regions) {
    const last = merged[merged.length - 1];
    if (last && r.start <= last.end + END_EPS_M)
      last.end = Math.max(last.end, r.end);
    else merged.push({ ...r });
  }
  return merged;
};

/** Cuts (centreline frame) where an opening or shower region reaches the ceiling. */
const crownCuts = (wall, openings, len, height, ceilingY) => {
  const cuts = [];
  for (const o of openings || []) {
    const ow = o?.dimensions?.x;
    const top = (o?.center?.y ?? 0) + (o?.dimensions?.y ?? 0) / 2;
    if (!(ow > 0) || top < ceilingY - CROWN_CLEARANCE_M) continue;
    const a = objAlongWall(o, wall);
    cuts.push([a - ow / 2, a + ow / 2]);
  }
  for (const r of wall?.texture?.regions || []) {
    if (r?.source !== 'shower') continue;
    const { a0, a1, y1 } = regionRectOnWall(r, len, height);
    if (y1 < height - CROWN_CLEARANCE_M) continue;
    if (a1 > a0) cuts.push([a0 - len / 2, a1 - len / 2]);
  }
  return cuts;
};

const crownForWall = ({ wall, openings, floorY, room }) => {
  if (wall?.trim?.hasCrownMolding !== true) return null;
  if (!wallHasFlatTop(wall)) return null;
  const axis = wallStartAxis(wall);
  if (!axis) return null;
  const len = wallLengthM(wall);
  const height = wall?.dimensions?.y || 0;
  const ceilingY = floorY + height;
  const style = trimStyleOf(wall.trim);
  const end = {
    x: axis.origin.x + axis.dir.x * len,
    z: axis.origin.z + axis.dir.z * len,
  };
  const cabinetCuts = cabinetRegionsOnWall(
    wall.id,
    axis.origin,
    end,
    ceilingY,
    style,
    room
  ).map((c) => [c.start - len / 2, c.end - len / 2]);
  const cuts = [
    ...crownCuts(wall, openings, len, height, ceilingY),
    ...cabinetCuts,
  ];
  const spans = subtractIntervals([-len / 2, len / 2], unionIntervals(cuts))
    .filter(([s, e]) => e - s >= MIN_SPAN_M)
    .map(([s, e]) => [s + len / 2, e + len / 2]);
  return {
    wallId: wall.id,
    lenM: len,
    axis,
    spans,
    style,
    colour: wall.trim.crownColor ?? null,
  };
};

module.exports = {
  CROWN_CLEARANCE_M,
  cabinetRegionsOnWall,
  crownForWall,
};
